import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useWholesaleAuth } from '../context/WholesaleAuthContext';
import { useWholesaleCart } from '../context/WholesaleCartContext';
import { API, normalizeRenderableImageUrl } from '../lib/api';

const LOGO_URL = "/images/wayside-logo-horizontal.png";

const WholesaleProductsPage = () => {
    const { customer, logout, getAuthHeaders } = useWholesaleAuth();
    const { addItem, itemCount } = useWholesaleCart();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const [prodRes, catRes] = await Promise.all([
                    axios.get(`${API}/wholesale/products`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/wholesale/categories`, { headers: getAuthHeaders() })
                ]);
                setProducts(prodRes.data);
                setCategories(catRes.data);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [getAuthHeaders]);

    const visibleProducts = useMemo(() => {
        if (activeCategory === 'all') return products;
        return products.filter((product) => (product.wholesale_category || product.category) === activeCategory);
    }, [products, activeCategory]);

    return (
        <div className="min-h-screen bg-maple-secondary">
            <section className="bg-maple-dark text-white border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                    <div>
                        <img src={LOGO_URL} alt="Wayside Maple" className="h-10 w-auto brightness-0 invert mb-4" />
                        <p className="text-white/50 uppercase tracking-[0.2em] text-xs">Wholesale Portal</p>
                        <h1 className="font-anton text-4xl mt-2">BATCH ORDERING</h1>
                        <p className="text-white/60 mt-3">Welcome back{customer?.full_name ? `, ${customer.full_name}` : ''}. Choose your wholesale products and batch sizes below.</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/wholesale/orders" className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.2em] hover:bg-white hover:text-maple-dark transition-colors">
                            Order History
                        </Link>
                        <Link to="/wholesale/cart" className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.2em] hover:bg-white hover:text-maple-dark transition-colors">
                            Cart ({itemCount})
                        </Link>
                        <button onClick={logout} className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.2em] hover:bg-white hover:text-maple-dark transition-colors">
                            Sign Out
                        </button>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-10">
                <div className="flex flex-wrap gap-3 mb-8">
                    <button onClick={() => setActiveCategory('all')} className={`px-4 py-2 border text-sm uppercase tracking-[0.2em] ${activeCategory === 'all' ? 'bg-maple-dark text-white border-maple-dark' : 'bg-white border-black/10 text-maple-dark'}`}>
                        All Categories
                    </button>
                    {categories.map((category) => (
                        <button key={category} onClick={() => setActiveCategory(category)} className={`px-4 py-2 border text-sm uppercase tracking-[0.2em] ${activeCategory === category ? 'bg-maple-dark text-white border-maple-dark' : 'bg-white border-black/10 text-maple-dark'}`}>
                            {category}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="py-24 text-center text-maple-dark/60">Loading wholesale catalog...</div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {visibleProducts.map((product) => {
                            const image = normalizeRenderableImageUrl(product.main_image) || normalizeRenderableImageUrl(product.images?.[0]);
                            return (
                                <div key={product.id} className="bg-white border border-black/10 overflow-hidden">
                                    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
                                        <div className="aspect-square bg-maple-secondary overflow-hidden">
                                            {image ? (
                                                <img src={image} alt={product.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-maple-dark/30 text-sm uppercase tracking-[0.2em]">No Image</div>
                                            )}
                                        </div>
                                        <div className="p-6 flex flex-col justify-between gap-5">
                                            <div>
                                                <p className="text-maple-red text-xs uppercase tracking-[0.25em]">{product.wholesale_category || product.category}</p>
                                                <h2 className="font-anton text-3xl text-maple-dark mt-2">{product.name}</h2>
                                                <p className="text-maple-dark/65 mt-4 leading-relaxed">{product.description}</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="border border-black/10 p-4">
                                                    <p className="text-maple-dark/50 text-xs uppercase tracking-[0.2em]">Batch Size</p>
                                                    <p className="font-anton text-2xl text-maple-dark mt-2">{product.wholesale_batch_size || '—'}</p>
                                                    <p className="text-maple-dark/60 text-sm mt-1">{product.wholesale_unit_label || 'units'}</p>
                                                </div>
                                                <div className="border border-black/10 p-4">
                                                    <p className="text-maple-dark/50 text-xs uppercase tracking-[0.2em]">Wholesale Price</p>
                                                    <p className="font-anton text-2xl text-maple-dark mt-2">{product.wholesale_price != null ? `$${Number(product.wholesale_price).toFixed(2)}` : '—'}</p>
                                                    <p className="text-maple-dark/60 text-sm mt-1">per batch</p>
                                                </div>
                                            </div>
                                            <button onClick={() => addItem(product, 1)} className="btn-primary w-full mt-5">
                                                <span>Add Batch to Cart</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};

export default WholesaleProductsPage;
